import { UserManagementData } from '../models/user-management-data';
import { ToastService } from '../services/toast.service';
import {
  CreateMobileUserDTO,
  CreateProxyUserDTO,
  CreateThirdPartyUserDTO,
  CreateUserDTO,
  FilterDTO,
  FilterOperator,
  PagedResultDTOOfUserInfoDTO, ParkerCreationType, Role,
  SortDirection,
  UpdateProxyUserDTO,
  UpdateThirdPartyUserDTO,
  UpdateUserInfoDTO,
  UserAccountClient,
  UserInfoDTO,
} from '../models/models.swagger.generated';
import { catchError, tap } from 'rxjs/operators';
import { ClientService } from '../services/client/client.service';
import { IAdaptorService } from '../shared/syncfusion/adaptor-service.interface';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { ShowInactiveService } from '../shared/grid-base/show-inactive.service';
import { ErrorType } from 'src/app/core/routing/error-types';

@Injectable({
  providedIn: 'root'
})
export class UserManagementService extends ShowInactiveService implements IAdaptorService {

  public lastAddedUserId: string;

  constructor(
    private userAccountClient: UserAccountClient,
    private clientService: ClientService,
    private toastService: ToastService
    ) {
      super();
    }


  // eslint-disable-next-line max-len
  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<PagedResultDTOOfUserInfoDTO> {
    const selectedClient = this.clientService.selectedClient?.id;

    if (!filters) {
      filters = [];
    }
    if (!this.showInactive) {
      filters.push(new FilterDTO({isComplex: false, field: 'Enabled', operator: FilterOperator.Equal, value: 'true'}));
    }

    filters.push(new FilterDTO({isComplex: false, field: 'LinkedParkers.CreationType', operator: FilterOperator.NotEqual, value: ParkerCreationType.AutomaticallyCreatedByUser.toString()}));

    return this.userAccountClient.searchUsers(filters, selectedClient, skip, take, sortBy, sortDirection).pipe(
      tap((result) => {
        if (result.items) {
          result.items.forEach((item) => {
            if (!item.client) {
              item.client = {name: ''} as any;
            }
          });
        }
      })
    );
  }

  public updateRecord(data: UserManagementData): Observable<UserInfoDTO> {
    if (data.data.roles?.includes(Role.Proxy)) {
      return this.userAccountClient.updateProxyUser(new UpdateProxyUserDTO(data.data)).pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.core.messages.user-edit.success-title',
            'i18n.core.messages.user-edit.success-message');
        }),
        catchError(err => {
          this.toastService.showApiExeption(err,
            'i18n.core.messages.user-edit.title',
            'i18n.core.messages.user-edit.message');
          return throwError(() => err);
        })
      );
    } else if (data.data.roles?.includes(Role.ThirdPartyParkingControl)) {
      return this.userAccountClient.updateThirdPartyUser(new UpdateThirdPartyUserDTO(data.data)).pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.core.messages.user-edit.success-title',
            'i18n.core.messages.user-edit.success-message');
        }),
        catchError(err => {
          this.toastService.showApiExeption(err,
            'i18n.core.messages.user-edit.title',
            'i18n.core.messages.user-edit.message');
          return throwError(() => err);
        })
      );
    }

    return this.userAccountClient.updateUser(new UpdateUserInfoDTO(data.data)).pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.core.messages.user-edit.success-title',
          'i18n.core.messages.user-edit.success-message');
      }),
      catchError(err => {
        this.toastService.showApiExeption(err,
          'i18n.core.messages.user-edit.title',
          'i18n.core.messages.user-edit.message');
        return throwError(() => err);
      })
    );
  }

  public addRecord(data: UserManagementData): Observable<UserInfoDTO> {
    if (data.data.roles?.includes(Role.ParkingRanger)) {
      return this.userAccountClient.addParkingRangerUser(new CreateMobileUserDTO(data.data)).pipe(
        tap((userInfo: UserInfoDTO) => {
          this.lastAddedUserId = userInfo.userId;
          this.toastService.showSuccess(
            'i18n.core.messages.user-add.success-title',
            'i18n.core.messages.user-add.success-message');
        }),
        catchError(err => {
          this.toastService.showApiExeption(err,
            'i18n.core.messages.user-add.title',
            'i18n.core.messages.user-add.message');
          return throwError(() => err);
        })
      );
    } else if (data.data.roles?.includes(Role.Proxy)) {
      return this.userAccountClient.addProxyUser(new CreateProxyUserDTO(data.data)).pipe(
        tap((userInfo: UserInfoDTO) => {
          this.lastAddedUserId = userInfo.userId;
          this.toastService.showSuccess(
            'i18n.core.messages.user-add.success-title',
            'i18n.core.messages.user-add.success-message');
        }),
        catchError(err => {
          this.toastService.showApiExeption(err,
            'i18n.core.messages.user-add.title',
            'i18n.core.messages.user-add.message');
          return throwError(() => err);
        })
      );
    } else if (data.data.roles?.includes(Role.ThirdPartyParkingControl)) {
      return this.userAccountClient.addThirdPartyUser(new CreateThirdPartyUserDTO(data.data)).pipe(
        tap((userInfo: UserInfoDTO) => {
          this.lastAddedUserId = userInfo.userId;
          this.toastService.showSuccess(
            'i18n.core.messages.user-add.success-title',
            'i18n.core.messages.user-add.success-message');
        }),
        catchError(err => {
          this.toastService.showApiExeption(err,
            'i18n.core.messages.user-add.title',
            'i18n.core.messages.user-add.message');
          return throwError(() => err);
        })
      );
    } else {
      return this.userAccountClient.addUser(new CreateUserDTO(data.data)).pipe(
        tap((userInfo: UserInfoDTO) => {
          this.lastAddedUserId = userInfo.userId;
          this.toastService.showSuccess(
            'i18n.core.messages.user-add.success-title',
            'i18n.core.messages.user-add.success-message');
        }),
        catchError(err => {
          this.toastService.showApiExeption(err,
            'i18n.core.messages.user-add.title',
            'i18n.core.messages.user-add.message');
          return throwError(() => err);
        })
      );
    }
  }

  public deleteRecord(id: number): Observable<any> {
    return this.userAccountClient.deleteUserByAdmin(id as any)
      .pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.core.messages.user-delete.success-title',
            'i18n.core.messages.user-delete.success-message'
          );
        }),
        catchError((error: any) => {
          this.toastService.showApiExeption(error,
            'i18n.core.messages.user-delete.title',
            'i18n.core.messages.user-delete.message');
          return throwError(() => error);
        })
      );
  }

  public reset2FARegistration(userId: string): Observable<void> {
    return this.userAccountClient.reset2FARegistration(userId).pipe(
      tap(() => {
        this.toastService.showSuccess('i18n.base.user-management.messages.reset-2fa-success.title', 'i18n.base.user-management.messages.reset-2fa-success.message');
      }),
      catchError((error: any) => {
        this.toastService.showError('i18n.base.user-management.messages.reset-2fa-failed.title', 'i18n.base.user-management.messages.reset-2fa-failed.message', null, ErrorType.serverError, error.response);
        return throwError(() => error);
      }),
    );
  }
}
